import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Hero from '../common/Hero';

const HeritageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Section = styled.section`
  margin: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #333;
  font-family: 'Playfair Display', serif;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: #6c5ce7;
  }
`;

const Introduction = styled.div`
  margin-bottom: 3rem;
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #555;
  }
`;

const TraditionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const TraditionCard = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  
  .tradition-image {
    height: 250px;
    background-size: cover;
    background-position: center;
  }
  
  .tradition-content {
    padding: 1.5rem;
    
    h3 {
      margin-bottom: 1rem;
      font-size: 1.5rem;
    }
    
    p {
      color: #666;
      line-height: 1.6;
    }
  }
`;

const RecipesSection = styled.div`
  margin-top: 4rem;
`;

const RecipeCard = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
    height: 300px;
  }
  
  .recipe-image {
    flex: 1;
    background-size: cover;
    background-position: center;
    min-height: 250px;
  }
  
  .recipe-content {
    flex: 1;
    padding: 2rem;
    
    h3 {
      margin-bottom: 1rem;
      font-size: 1.5rem;
    }
    
    .recipe-description {
      color: #666;
      line-height: 1.6;
      margin-bottom: 1.5rem;
    }
    
    .recipe-details {
      display: flex;
      margin-bottom: 1.5rem;
      
      .detail {
        margin-right: 2rem;
        
        span {
          display: block;
          font-weight: 600;
          color: #6c5ce7;
        }
      }
    }
    
    button {
      background: #6c5ce7;
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 5px;
      cursor: pointer;
      font-weight: 500;
      transition: background 0.3s ease;
      
      &:hover {
        background: #5a4ad1;
      }
    }
  }
`;

const RecipeModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const RecipeModalContent = styled(motion.div)`
  background: white;
  border-radius: 10px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  position: relative;
  
  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }
  
  h2 {
    margin-bottom: 1.5rem;
    color: #333;
  }
  
  .recipe-section {
    margin-bottom: 2rem;
    
    h3 {
      color: #6c5ce7;
      margin-bottom: 1rem;
    }
    
    ul, ol {
      padding-left: 1.5rem;
      
      li {
        margin-bottom: 0.5rem;
      }
    }
  }
`;

const CulturalHeritage = () => {
    const [selectedRecipe, setSelectedRecipe] = useState(null);

    const traditions = [
        {
            id: 1,
            title: 'Identity and Clan Names',
            description: 'The Mlalazi Clan is also known as Ngalonde, Dumane, Mkhandela, Manxeba, and Ngwane.\n' +
                '\n' +
                'Clan praises (izibongo) play a vital role in preserving and expressing heritage and pride.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/mlalazi.jpg'
        },
        {
            id: 2,
            title: 'Oral Tradition and Genealogy',
            description: 'History is preserved through oral accounts, folklore, and izibongo.\n' +
                '\n' +
                'Emphasis on recording family trees and chiefly lineages.\n' +
                '\n' +
                'Family migration stories link current settlements back to KwaZulu-Natal, Botswana, and early Zimbabwean regimental posts.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/story.jpg'
        },
        {
            id: 3,
            title: 'Blacksmithing and Military Craft',
            description: 'Mlalazi people were historically blacksmiths and spear-makers, central to the military economy of the Ndebele state.\n' +
                '\n' +
                'They held roles in military engineering and contributed to crafting “imikhonto yamakhosi” (royal spears).',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/spear.jpg'
        },
        {
            id: 4,
            title: 'Settlement Architecture & Homesteads',
            description: 'Ndebele town structures were circular, with royal enclosures, defensive palisades, cattle kraals, and designated huts based on seniority.\n' +
                '\n' +
                'Traditional Ndebele homes (iguma) were elaborately painted, often by women, as a form of identity and communication.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/homestead.jpg'
        },
        {
            id: 5,
            title: 'Music, Dance, and Performing Arts',
            description: 'Traditional dances like:\n' +
                '\n' +
                'Mushongoyo – pre- or post-war male dance.\n' +
                '\n' +
                'Isitshikitsha – drought-time rainmaking dance.\n' +
                '\n' +
                'Amabhiza and Ingquzu – performed at social events.\n' +
                '\n' +
                'Accompanied by singing, clapping, ululation, whistling—a rich oral performance culture.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/Ingquzu.webp'
        },
        {
            id: 6,
            title: 'Rituals and Religion',
            description: 'Belief in uNkulunkulu (God) accessed through ancestors (amadlozi).\n' +
                '\n' +
                'Ceremonies like:\n' +
                '\n' +
                'Umbuyiso – reintroducing a deceased to ancestral spirits.\n' +
                '\n' +
                'Inxwala – first-fruits ceremony with military review and dances.\n' +
                '\n' +
                'Rainmaking rituals were community-wide spiritual events.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/ceremony.jpg'
        },
        {
            id: 7,
            title: 'Child Rearing and Protection',
            description: 'Practices like:\n' +
                '\n' +
                'Ukutaba umntwana – strengthening and protecting infants.\n' +
                '\n' +
                'Ukugqizisa intebe – wearing protective charms to guard against evil spirits.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/ukutaba.webp'
        },
        {
            id: 8,
            title: 'Education and Initiation',
            description: 'Boys and girls had structured initiation systems:\n' +
                '\n' +
                'Boys: arms training, duty to nation, and discipline.\n' +
                '\n' +
                'Girls: domestic skills, land cultivation, motherhood.\n' +
                '\n' +
                'Early childhood learning involved riddles, songs, folktales, and poetry.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/kids.jpg'
        },
        {
            id: 9,
            title: 'Marriage and Family Practices',
            description: 'Lobola (amalobolo) system still practiced, though modernized (includes groceries and Western-style weddings).\n' +
                '\n' +
                'Umthimba (wedding ceremony) traditionally followed soon after lobola.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/marriage.webp'
        },
        {
            id: 10,
            title: 'Funeral Customs',
            description: 'Funerals were family-led and solemn.\n' +
                '\n' +
                'After a year, umbuyiso brought the spirit of the deceased back into the family’s ancestral line.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/funeral.jpg'
        },
        {
            id: 11,
            title: 'Preservation and Modern Regrouping',
            description: 'Cultural revival efforts include:\n' +
                '\n' +
                'Clan Constitution and strategic plans.\n' +
                '\n' +
                'Regular Clan Gatherings (first held in 2011).\n' +
                '\n' +
                'Youth engagement, business development, language and cultural preservation, and diaspora inclusion.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/dumane.jpg'
        }
    ];

    const recipes = [
        {
            id: 1,
            title: 'Isitshwala (Sadza / Thick Maize Porridge)',
            description: 'A beloved staple in Ndebele and Mlalazi homes, traditionally served with meat or fermented milk.',
            prepTime: '5 mins',
            cookTime: '20 mins',
            servings: '4',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/isitshwala.jpg',
            ingredients: [
                '2 cups maize meal (mealie meal)',
                '4 cups water (plus extra for thickening)',
                'Pinch of salt (optional)'
            ],
            instructions: [
                'Bring 3 cups of water to a boil in a pot.',
                'Mix ½ cup of maize meal with 1 cup of cold water to make a slurry.',
                'Stir the slurry into the boiling water, reduce heat, and simmer for 5 minutes.',
                'Slowly add remaining maize meal while stirring vigorously to prevent lumps.',
                'Stir until thick and smooth (about 10–15 minutes).',
                'Cover and steam on low heat for 5 minutes before serving.'
            ]
        },
        {
            id: 2,
            title: 'Traditional Beef Stew (Inyama Yenkomo)',
            description: 'A hearty and flavorful beef stew cooked with simple ingredients, popular in clan feasts.',
            prepTime: '15 mins',
            cookTime: '1 hr',
            servings: '4',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/inyama.jpg',
            ingredients: [
                '1 kg beef (on-the-bone preferred)',
                '1 onion, chopped',
                '2 tomatoes, chopped',
                'Salt to taste',
                'Water',
                'Optional: garlic, curry powder, green pepper'
            ],
            instructions: [
                'Boil beef in a pot with water until tender (about 45–60 minutes).',
                'Drain excess water; fry beef in its own fat or add a little oil.',
                'Add onion and sauté until translucent.',
                'Add tomatoes and cook until they form a sauce.',
                'Season and simmer until stew thickens.'
            ]
        },
        {
            id: 3,
            title: 'Pumpkin Leaves with Peanut Sauce (Ibhobola)',
            description: 'A nutritious traditional side dish made from fresh pumpkin leaves and crushed peanuts.',
            prepTime: '10 mins',
            cookTime: '20 mins',
            servings: '4',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/ibhobola.jpg',
            ingredients: [
                '2 cups pumpkin leaves, chopped',
                '1 onion, finely chopped',
                '½ cup ground peanuts (roasted, unsalted)',
                'Salt',
                'Water or a bit of cooking oil'
            ],
            instructions: [
                'Wash and boil pumpkin leaves in a little water until soft (10–15 min).',
                'Drain excess water.',
                'In a pan, sauté onion until soft.',
                'Add leaves and ground peanuts.',
                'Stir and simmer gently until thick and creamy.',
                'Optional: Add a spoonful of peanut butter for richness.'
            ]
        },
        {
            id: 4,
            title: 'Umqombothi (Traditional Sorghum Beer)',
            description: 'A fermented sorghum and maize beer central to clan ceremonies like Inxwala and Umbuyiso.',
            prepTime: '20 mins (plus fermentation time)',
            cookTime: '1 hr boil + 12–24 hrs fermentation',
            servings: '10',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/umqombothi.jpg',
            ingredients: [
                '1 kg sorghum meal',
                '1 kg maize meal',
                '1 cup malted sorghum',
                '4–5 litres water'
            ],
            instructions: [
                'Mix sorghum and maize meal with warm water in a large container.',
                'Cover and leave to ferment for 2–3 days in a warm place.',
                'On day 3, boil the mixture for 1 hour. Cool completely.',
                'Add malted sorghum and stir well.',
                'Let ferment again for another 12–24 hours until frothy.',
                'Strain and serve immediately while fresh.'
            ]
        },
        {
            id: 5,
            title: 'Roasted Meat (Inyama Ewosiweyo)',
            description: 'A ceremonial dish enjoyed at weddings, clan gatherings, and royal feasts.',
            prepTime: '1 hr (marination)',
            cookTime: '1.5 hrs',
            servings: '6',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/braai.jpg',
            ingredients: [
                '1.5 kg goat or beef cuts (ribs or leg)',
                'Salt',
                'Optional: garlic, lemon juice, herbs'
            ],
            instructions: [
                'Marinate meat with salt (and optional ingredients) for at least 1 hour.',
                'Roast over open flame, coals, or in an oven at 180°C for 90 minutes.',
                'Turn occasionally until crispy on the outside and tender inside.'
            ]
        },
        {
            id: 6,
            title: 'Amasi (Fermented Milk)',
            description: 'A nourishing fermented milk drink, often paired with isitshwala or used in rituals.',
            prepTime: '5 mins',
            cookTime: 'Fermentation: 2–3 days',
            servings: '4',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/amasi.jpg',
            ingredients: [
                '1 litre unpasteurized full cream milk (or raw cow’s milk)'
            ],
            instructions: [
                'Pour milk into a clay pot or sealed plastic container.',
                'Cover with a clean cloth and leave in a warm place for 2–3 days.',
                'Milk will ferment and separate into curds and whey.',
                'Stir and chill before serving.'
            ]
        }
    ];

    return (
        <>
            <Hero
                title="Cultural Heritage"
                subtitle="Preserving our traditions and passing them to future generations"
                backgroundImage="assets/images/logo.jpg"
            />

            <HeritageContainer>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Introduction>
                        <SectionTitle>Our Cultural Identity</SectionTitle>
                        <p>
                            Our clan's cultural heritage is a vibrant tapestry woven from diverse traditions, customs, and values.
                            Through generations, we have preserved these cultural touchstones while adapting to new environments and
                            influences. This rich heritage shapes our identity, provides us with a sense of belonging, and connects
                            us to our ancestors. Explore the traditions, recipes, and customs that make our clan unique.
                        </p>
                    </Introduction>

                    <Section>
                        <SectionTitle>Clan Traditions</SectionTitle>
                        <TraditionsGrid>
                            {traditions.map(tradition => (
                                <TraditionCard
                                    key={tradition.id}
                                    whileHover={{ y: -10 }}
                                >
                                    <div className="tradition-image" style={{ backgroundImage: `url(${tradition.image})` }}></div>
                                    <div className="tradition-content">
                                        <h3>{tradition.title}</h3>
                                        <p>{tradition.description}</p>
                                    </div>
                                </TraditionCard>
                            ))}
                        </TraditionsGrid>
                    </Section>

                    <RecipesSection>
                        <SectionTitle>Clan Recipes</SectionTitle>
                        {recipes.map(recipe => (
                            <RecipeCard
                                key={recipe.id}
                                whileHover={{ y: -5 }}
                            >
                                <div className="recipe-image" style={{ backgroundImage: `url(${recipe.image})` }}></div>
                                <div className="recipe-content">
                                    <h3>{recipe.title}</h3>
                                    <p className="recipe-description">{recipe.description}</p>
                                    <div className="recipe-details">
                                        <div className="detail">
                                            <span>Prep</span>
                                            {recipe.prepTime}
                                        </div>
                                        <div className="detail">
                                            <span>Cook</span>
                                            {recipe.cookTime}
                                        </div>
                                        <div className="detail">
                                            <span>Serves</span>
                                            {recipe.servings}
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedRecipe(recipe)}>View Recipe</button>
                                </div>
                            </RecipeCard>
                        ))}
                    </RecipesSection>
                </motion.div>
            </HeritageContainer>

            {selectedRecipe && (
                <RecipeModal onClick={() => setSelectedRecipe(null)}>
                    <RecipeModalContent
                        onClick={e => e.stopPropagation()}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <button className="close-button" onClick={() => setSelectedRecipe(null)}>✕</button>
                        <h2>{selectedRecipe.title}</h2>
                        <div className="recipe-section">
                            <h3>Ingredients</h3>
                            <ul>
                                {selectedRecipe.ingredients.map((ingredient, index) => (
                                    <li key={index}>{ingredient}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="recipe-section">
                            <h3>Instructions</h3>
                            <ol>
                                {selectedRecipe.instructions.map((instruction, index) => (
                                    <li key={index}>{instruction}</li>
                                ))}
                            </ol>
                        </div>
                    </RecipeModalContent>
                </RecipeModal>
            )}
        </>
    );
};

export default CulturalHeritage;